import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  onboardingCompletionResponseSchema,
  onboardingProfileResponseSchema,
} from "@/schemas/onboarding";
import type {
  OnboardingCompletionResponse,
  OnboardingProfileRequest,
  OnboardingProfileResponse,
  RoleExperienceRequest,
} from "@/schemas/onboarding";

import { apiRequest } from "./apiUtils";

export const onboardingKeys = {
  me: ["onboarding", "me"] as const,
};

export async function getOnboardingProfile(): Promise<OnboardingProfileResponse> {
  const res = await apiRequest<unknown>("onboarding/me", { method: "GET" });
  return onboardingProfileResponseSchema.parse(res);
}

export async function saveOnboardingProfile(
  body: OnboardingProfileRequest,
): Promise<OnboardingProfileResponse> {
  const res = await apiRequest<unknown>("onboarding/me", {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return onboardingProfileResponseSchema.parse(res);
}

export async function completeOnboardingProfile(): Promise<OnboardingCompletionResponse> {
  const res = await apiRequest<unknown>("onboarding/me/complete", {
    method: "POST",
  });
  return onboardingCompletionResponseSchema.parse(res);
}

export function useOnboardingDraft() {
  return useQuery<OnboardingProfileResponse>({
    queryKey: onboardingKeys.me,
    queryFn: getOnboardingProfile,
  });
}

export function useSaveOnboarding() {
  const qc = useQueryClient();
  return useMutation<OnboardingProfileResponse, Error, OnboardingProfileRequest>({
    mutationFn: saveOnboardingProfile,
    onSuccess: (data) => qc.setQueryData(onboardingKeys.me, data),
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation<OnboardingCompletionResponse, Error, void>({
    mutationFn: completeOnboardingProfile,
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.me }),
  });
}

export type SaveExperienceRequest = { roleExperiences: Array<RoleExperienceRequest> };

export async function saveOnboardingExperience(
  body: SaveExperienceRequest,
): Promise<OnboardingProfileResponse | null> {
  // PUT /onboarding/me/experience takes a bare array (List<RoleExperienceRequest>).
  // It now returns the refreshed profile (like GET/PUT /me); older backends returned
  // 204 No Content. Tolerate both so frontend/backend deploy order doesn't matter.
  const res = await apiRequest<unknown>("onboarding/me/experience", {
    method: "PUT",
    body: JSON.stringify(body.roleExperiences),
  });
  const parsed = onboardingProfileResponseSchema.safeParse(res);
  return parsed.success ? parsed.data : null;
}

export function useSaveOnboardingExperience() {
  const qc = useQueryClient();
  return useMutation<
    OnboardingProfileResponse | null,
    Error,
    SaveExperienceRequest
  >({
    mutationFn: saveOnboardingExperience,
    onSuccess: (data) => {
      // Prime the cache from the response when present; otherwise (204) refetch.
      if (data) qc.setQueryData(onboardingKeys.me, data);
      else qc.invalidateQueries({ queryKey: onboardingKeys.me });
    },
  });
}
